import React, { useEffect, useCallback, useMemo } from "react"; // Adicionado useMemo
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import "../../../assets/styles/custom-styles.css";
import Form from "../../../components/forms/Form";
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import { supplierFields } from "../../../constants/forms/supplierFields";
import { maskCpfCnpj, removeMask, removeMaskCpfCnpj } from "../../../utils/maskUtils";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import useNotification from "../../../hooks/useNotification";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditSupplierPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      alias: [],
      name: [],
      cpf_cnpj: [],
      deleted_at: false,
    },
    "almox_suppliers",
  );

  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { formData, setFormData, handleChange, formatData } = useForm(
    setDefaultFieldValues(supplierFields),
  );

  // --- Query para buscar os detalhes do Fornecedor ---
  const {
    data: supplierData,
    isLoading: isLoadingSupplier,
    isError: isErrorSupplier,
    error: supplierQueryError,
  } = useQueryWithLoader({
    queryKey: ["almox_supplierDetails", id],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(
        entities.suppliers.getById(id),
      );
      return response.result;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  useEffect(() => {
    if (supplierData) {
      setFormData({
        ...supplierData,
        supplier: {
          ...supplierData,
        },
      });
    }
  }, [supplierData]);

  // --- Mutação para atualizar o Fornecedor ---
  const updateSupplierMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({}); // Limpa os erros de formulário antes de uma nova submissão
      const response = await baseServicePut(
        entities.suppliers.update(id),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        navigate("/almoxarifado/fornecedores/", { state: { type: "success" } });
        queryClient.invalidateQueries({
          queryKey: ["almox_supplierDetails", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["almox_suppliers", filters, pagination],
        });
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        supplier: {
          ...cleanedData.supplier,
          cpf_cnpj: removeMaskCpfCnpj(cleanedData.supplier.cpf_cnpj),
        },
      };
      await updateSupplierMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar fornecedor:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateSupplierMutation]);

  const handleBack = useCallback(() => {
    navigate("/almoxarifado/fornecedores/");
  }, [navigate]);

  // --- Efeito para exibir notificações de estado da rota (PADRÃO EditUnitPage) ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate("/almoxarifado/fornecedores/", { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  // --- Renderização do formulário (PADRÃO EditUnitPage) ---
  const renderForm = useCallback(() => {
    const renderedSections = new Set();

    return supplierFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={() => {}}
            getSelectedValue={() => {}}
          />
        </div>
      );
    });
  }, [formData, formErrors, handleChange]);

  // --- Consolidação dos estados de carregamento para o loader global do formulário ---
  const isPageLoading = isLoadingSupplier || updateSupplierMutation.isPending;

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Fornecedor"
        showBackButton={true}
        backUrl="/almoxarifado/fornecedores/"
      />
      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Atualizar"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={isPageLoading}
        >
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditSupplierPage;
